import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";

import useRequest from "@/lib/request/useRequest";
import { sortArrayByKey } from "@/lib/utils/sorting";
import { GrowthModel } from "@/types/dssat";
import {
    getKeyValue,
    TableRow,
    Table,
    TableBody,
    TableColumn,
    TableHeader,
    Spinner,
    TableCell,
    SortDescriptor,
} from "@heroui/react";
import dayjs from "dayjs";
import { useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { roundTo } from "@/lib/utils/numbers";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";

function VegetativeTable() {
    const { formatDateMMDDYYYY } = useI18n();
    const { t } = useTranslation("insights");
    const { fieldId } = useFieldDashboard() ?? {};
    const { useGetRequest } = useRequest();
    const f = useFormContext();

    const [startDate, endDate] = f.watch(["startDate", "endDate"]);

    const growthModelQuery = useGetRequest<GrowthModel[]>({
        url: `/fields/${fieldId}/growth_model/`,
        enabled: !!fieldId,
        params: {
            start_date: dayjs(startDate).format("YYYY-MM-DD"),
            end_date: dayjs(endDate).format("YYYY-MM-DD"),
        },
    });

    const data = useMemo(() => {
        return sortArrayByKey(
            growthModelQuery.data?.map((item) => {
                return {
                    ...item,
                    date: formatDateMMDDYYYY(t(item.capture_date)),
                    ndre_mean: roundTo(item.ndre_mean, 4),
                    ndvi_mean: roundTo(item.ndvi_mean, 4),
                    ndre_min: roundTo(item.ndre_min, 4),
                    ndre_max: roundTo(item.ndre_max, 4),
                    ndvi_min: roundTo(item.ndvi_min, 4),
                    ndvi_max: roundTo(item.ndvi_max, 4),
                };
            }) ?? [],
            "capture_date",
        );
    }, [growthModelQuery.data, formatDateMMDDYYYY, t]);

    const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
        column: "date",
        direction: "ascending",
    });
    const sortedItems = useMemo(() => {
        const { column, direction } = sortDescriptor;
        if (!column) return data;
        const sorted = sortArrayByKey(
            data,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            column as any,
            direction === "descending" ? "desc" : "asc",
        );
        return sorted;
    }, [data, sortDescriptor]);

    const COLUMNS = useMemo(
        () => [
            { key: "date", label: t("tableColumns.date") },
            { key: "ndre_mean", label: t("tableColumns.ndreMean") },
            { key: "ndre_min", label: t("tableColumns.ndreMin") },
            { key: "ndre_max", label: t("tableColumns.ndreMax") },
            { key: "ndvi_mean", label: t("tableColumns.ndviMean") },
            { key: "ndvi_min", label: t("tableColumns.ndviMin") },
            { key: "ndvi_max", label: t("tableColumns.ndviMax") },
        ],
        [t],
    );

    return (
        <Table
            isHeaderSticky
            isVirtualized
            maxTableHeight={500}
            aria-label={t("ariaLabels.trendline")}
            onSortChange={setSortDescriptor}
            sortDescriptor={sortDescriptor}
            classNames={{
                th: "px-[0.25rem]",
                td: "px-[0.25rem]",
                wrapper: "p-0 shadow-none rounded-none",
            }}
        >
            <TableHeader columns={COLUMNS}>
                {(column) => (
                    <TableColumn key={column.key} allowsSorting>
                        {column.label}
                    </TableColumn>
                )}
            </TableHeader>
            <TableBody
                items={sortedItems}
                emptyContent={t("messages.noDataDateRange")}
                isLoading={growthModelQuery.isFetching}
                loadingContent={<Spinner label={t("messages.loading")} />}
            >
                {(item) => (
                    <TableRow
                        key={item.image_id}
                        className="hover:bg-gray-100 rounded-lg"
                    >
                        {(columnKey) => {
                            return (
                                <TableCell className="text-xs">
                                    {getKeyValue(item, columnKey)}
                                </TableCell>
                            );
                        }}
                    </TableRow>
                )}
            </TableBody>
        </Table>
    );
}

export default VegetativeTable;
